/**
 * API Client Utility
 *
 * Centralized API client for making HTTP requests to the backend API.
 * Handles base URL configuration and common request patterns.
 *
 * Location: src/utils/apiClient.js
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';
const API_VERSION = import.meta.env.VITE_API_VERSION || 'v1';

/**
 * Build full API URL from path
 *
 * @param {string} path - API path (e.g., '/experiences/123/routes')
 * @returns {string} Full API URL
 */
function buildApiUrl(path) {
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `${API_BASE_URL}/api/${API_VERSION}/${cleanPath}`;
}

/**
 * Make API request with error handling
 *
 * @param {string} path - API path
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} Response data
 * @throws {Error} API error with message
 */
async function apiRequest(path, options = {}) {
  const url = buildApiUrl(path);

  // Get access token from localStorage
  const accessToken = localStorage.getItem('accessToken');

  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    },
    credentials: 'include', // Send cookies with requests
  };

  // Add Authorization header if token exists
  if (accessToken) {
    defaultOptions.headers.Authorization = `Bearer ${accessToken}`;
  }

  const mergedOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers
    }
  };

  try {
    const response = await fetch(url, mergedOptions);

    // Parse JSON response
    const data = await response.json();

    // Check for API errors
    if (!response.ok) {
      throw new Error(data.message || (typeof data.error === 'string' ? data.error : data.error?.message) || `API error: ${response.status}`);
    }

    return data;
  } catch (error) {
    // Re-throw with context
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Network request failed');
  }
}

/**
 * GET request
 *
 * @param {string} path - API path
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} Response data
 */
export async function get(path, options = {}) {
  return apiRequest(path, {
    ...options,
    method: 'GET'
  });
}

/**
 * POST request
 *
 * @param {string} path - API path
 * @param {Object} data - Request body data
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} Response data
 */
export async function post(path, data = null, options = {}) {
  return apiRequest(path, {
    ...options,
    method: 'POST',
    body: data ? JSON.stringify(data) : null
  });
}

/**
 * PUT request
 *
 * @param {string} path - API path
 * @param {Object} data - Request body data
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} Response data
 */
export async function put(path, data = null, options = {}) {
  return apiRequest(path, {
    ...options,
    method: 'PUT',
    body: data ? JSON.stringify(data) : null
  });
}

/**
 * PATCH request
 *
 * @param {string} path - API path
 * @param {Object} data - Request body data
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} Response data
 */
export async function patch(path, data = null, options = {}) {
  return apiRequest(path, {
    ...options,
    method: 'PATCH',
    body: data ? JSON.stringify(data) : null
  });
}

/**
 * DELETE request
 *
 * @param {string} path - API path
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} Response data
 */
export async function del(path, options = {}) {
  return apiRequest(path, {
    ...options,
    method: 'DELETE'
  });
}

/**
 * POST multipart/form-data request (for file uploads)
 *
 * @param {string} path - API path
 * @param {FormData} formData - Form data with files
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} Response data
 */
export async function postFormData(path, formData, options = {}) {
  // Don't set Content-Type header for FormData - browser will set it with boundary
  const { headers, ...restOptions } = options;

  return apiRequest(path, {
    ...restOptions,
    method: 'POST',
    body: formData,
    headers: {
      // Remove Content-Type header for FormData
      ...headers
    }
  });
}

export default {
  get,
  post,
  put,
  patch,
  del,
  postFormData
};
